import { useEffect, useState } from 'react';
import {
    NextBestAction,
    NextBestActionEnum,
    NextBestActionUpdateYourSituationTypeEnum,
    SigninUserV3Input,
    UserProfile
} from '../../../../graphql.generated';
import { UserWithNextBestAction } from '../../hooks/authMutationHook.types';
import { useSaveGuestSituationData } from '../../hooks/useSaveGuestSituationData';
import { YourSituationStepProps } from '../YourSituationStep';
import { setupLogger } from '@zoopla/logger';
import { SignInFormData } from '../SigninForm/SignInForm';

export type UseAuthProps = {
    stepsOrder: string[];
    onDone: () => void;
    /**
     * Executes when the current step has changed, you can use this for focus management and tracking
     */
    onStepMount?: (newStep: string) => void;
};

type YourSituationData = Pick<
    YourSituationStepProps,
    'questionType' | 'trackingIdentifier' | 'title' | 'initialValues'
>;

const logger = setupLogger({
    custodian: 'pnp',
    component: 'auth-modal'
});

// throw error if onboarding is before auth
const checkMandatorySteps = (steps: string[]) => {
    const authIndex = steps.findIndex((step) => step === 'auth');
    const onboardingIndex = steps.findIndex((step) => step === 'onboarding');

    if (authIndex === -1) {
        throw new Error('stepsOrder needs to specify "auth"');
    }

    if (onboardingIndex !== -1 && onboardingIndex < authIndex) {
        throw new Error('"onboarding" step needs to always follow "auth" step');
    }
};

const getSituationProps = (
    actions: NextBestAction[],
    profile?: UserProfile | null
): YourSituationData => {
    const nbaYourSituationIndex = actions.find(
        (item) => item.action === NextBestActionEnum.UpdateYourSituation
    );
    const yourSituationType =
        nbaYourSituationIndex?.extraDetails?.updateYourSituationType;

    switch (yourSituationType) {
        case NextBestActionUpdateYourSituationTypeEnum.Update:
            return {
                questionType: 'update',
                title: 'Update your situation',
                trackingIdentifier: 'update: auth: ',
                initialValues: profile || {}
            };
        case NextBestActionUpdateYourSituationTypeEnum.ResurfaceSeller:
            return {
                questionType: 'seller',
                title: 'Update your situation',
                trackingIdentifier: 'update: auth: ',
                initialValues: undefined
            };
        default:
            return {
                questionType: 'default',
                trackingIdentifier: 'auth: ',
                title: 'Customise your experience',
                initialValues: undefined
            };
    }
};

const getWeakPasswordStep = (steps: string[]) =>
    steps.reduce<string[]>((acc, currentStep) => {
        if (currentStep === 'auth') {
            return [...acc, 'auth', 'change-weak-password'];
        }

        return [...acc, currentStep];
    }, []);

const getOnboardingSteps = (
    steps: string[],
    onboardingSteps: ('preferences' | 'your-situation')[]
) =>
    steps.reduce<string[]>((acc, currentStep) => {
        if (currentStep === 'onboarding') {
            return [...acc, ...onboardingSteps];
        }

        return [...acc, currentStep];
    }, []);

const getStepsWithoutAuth = (steps: string[]) =>
    // if `auth` is not the first step in the array (aka the step that is currently shown)
    // then we want to remove any subsequent auth step
    steps.filter((currentStep, index) =>
        currentStep === 'auth' && index !== 0 ? false : true
    );

export const useAuthSteps = ({
    stepsOrder,
    onDone,
    onStepMount
}: UseAuthProps) => {
    checkMandatorySteps(stepsOrder);

    const [steps, setSteps] = useState<string[]>(stepsOrder);
    const currentStep = steps[0];

    const { hasSavedAnswers, saveSituationData, removeSavedAnswers } =
        useSaveGuestSituationData();

    const [signInData, setSignInData] = useState<SigninUserV3Input>({
        email: '',
        password: '',
        rememberMe: false
    });

    const [yourSituationStepData, setYourSituationStepData] =
        useState<YourSituationData>({
            questionType: 'default',
            trackingIdentifier: 'auth: ',
            title: 'Customise your experience',
            initialValues: undefined
        });

    const setRemainingStepsOrDone = (steps: string[]) => {
        const stepsCopy = [...steps];

        // remove current step
        stepsCopy.shift();

        // we never want to show the `onboarding` step, since it's just a placeholder
        // if we get there by mistake we skip it
        if (stepsCopy[0] === 'onboarding') {
            stepsCopy.shift();
        }

        if (stepsCopy.length === 0) {
            onDone();
            // we don't want to move away from the current step
            // any further logic should be handled in `onDone`
            return;
        }

        setSteps(stepsCopy);
    };

    const manageUserActions = async (user: UserWithNextBestAction) => {
        const actionsFromBackend = user.actions;
        const yourSituationActionIndex =
            actionsFromBackend.indexOf('your-situation');

        if (hasSavedAnswers()) {
            try {
                await saveSituationData();
                removeSavedAnswers();

                // remove the your-situation step since we've already updated for the current user
                if (yourSituationActionIndex > -1) {
                    actionsFromBackend.splice(yourSituationActionIndex, 1);
                }
            } catch (e) {
                logger.error({ error: e }, 'managing user actions');
            }
        }

        if (actionsFromBackend.length > 0) {
            if (actionsFromBackend.includes('your-situation')) {
                setYourSituationStepData(
                    getSituationProps(
                        user?.nextBestAction?.actions || [],
                        user?.profile
                    )
                );
            }
        }

        return actionsFromBackend;
    };

    const moveToNextStep = () => {
        setRemainingStepsOrDone(steps);
    };

    /**
     * This function is intended only to be used on the step for weak password
     */
    const enqueueWeakPasswordAndMoveSteps = async (data: SignInFormData) => {
        const stepsWithWeakPasswordStep = getWeakPasswordStep(steps);

        setSignInData(data);

        setRemainingStepsOrDone(stepsWithWeakPasswordStep);
    };

    /**
     * This function is intended only to be used on the steps that authenticate
     * i.e. `auth` step
     */
    const enqueueOnboardingAndMoveSteps = async (
        user: UserWithNextBestAction
    ) => {
        const actionsFromBackend = await manageUserActions(user);

        const stepsWithReplacedOnboarding = getOnboardingSteps(
            steps,
            actionsFromBackend
        );

        setRemainingStepsOrDone(stepsWithReplacedOnboarding);
    };

    /**
     * This function is intended only to be used on the steps that authenticate
     * shown previously to the `auth` step
     * i.e. `situation-quick-access` step
     */
    const enqueueOnboardingRemoveAuthAndMoveSteps = async (
        user: UserWithNextBestAction
    ) => {
        const actionsFromBackend = await manageUserActions(user);
        const stepsWithoutAuth = getStepsWithoutAuth(steps);

        const stepsWithReplacedOnboarding = getOnboardingSteps(
            stepsWithoutAuth,
            actionsFromBackend
        );

        setRemainingStepsOrDone(stepsWithReplacedOnboarding);
    };

    // this functionality is needed for when closing the modal
    const resetSteps = () => {
        setSteps(stepsOrder);
    };

    useEffect(() => {
        onStepMount?.(currentStep);
    }, [currentStep, onStepMount]);

    return {
        currentStep,
        moveToNextStep,
        enqueueWeakPasswordAndMoveSteps,
        enqueueOnboardingAndMoveSteps,
        enqueueOnboardingRemoveAuthAndMoveSteps,
        yourSituationStepData,
        resetSteps,
        signInData
    };
};
